import React from 'react';
import SplashCursor from '../components/SplashCursor';
import { Link } from 'react-router-dom';
import { Award, Users, Shield, Star } from 'lucide-react';

const Home = () => {
  const features = [
    {
      icon: <Award className="w-12 h-12 text-[#EE6A1F]" />,
      title: 'Magical Experience',
      description: 'Pioneers of Hot Air Ballooning in Kenya, knowledge of Uganda, passion for ballooning and Africa',
      link: '/the-adventure'
    },
    {
      icon: <Users className="w-12 h-12 text-[#EE6A1F]" />,
      title: 'WHY CHOOSE US?',
      description: 'Experience, Pilot skill, Languages',
      link: '/our-pilots'
    },
    {
      icon: <Star className="w-12 h-12 text-[#EE6A1F]" />,
      title: 'BUCKET LIST',
      description: 'Adventure of a Lifetime. Wow, wow, wow! Sunrise in Uganda',
      link: '/reviews-gallery'
    },
    {
      icon: <Shield className="w-12 h-12 text-[#EE6A1F]" />,
      title: 'SAFETY',
      description: 'Annual inspection by KCAA, Safety Processes, Head of Safety',
      link: '/our-balloons'
    }
  ];

  const reviews = [
    { text: 'What an incredible experience, one of the highlights of our trip.', year: '2024' },
    { text: 'Definitely worth it.', year: '2024' },
    { text: 'Amazing Flight', year: '2024' },
    { text: 'The brekkie afterwards was very good and very surreal eating in the middle of the bush!', year: '2024' }
  ];

  return (
    <>
      <SplashCursor />
      <div className="min-h-screen">
        {/* Hero Section */}
      <section
        className="relative h-screen flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1761976671459-8a27e6b78c09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxob3QlMjBhaXIlMjBiYWxsb29uJTIwc2FmYXJpfGVufDB8fHx8MTc2MzA3MTA4OHww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(101, 67, 33, 0.3)' }}></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-xl md:text-2xl tracking-widest mb-4 font-light">PIONEERS IN THE SKY</h1>
          <div className="text-7xl md:text-9xl font-bold mb-6 tracking-wider">
            EST.1976
          </div>
          <p className="text-xl md:text-2xl tracking-widest font-light">
            SAFETY - EXPERTISE - SERVICE - TRUST
          </p>
        </div>
      </section>

      {/* Introduction - 2 Column Layout */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                From the pioneers of ballooning in Uganda
              </h2>
              <p className="text-xl text-gray-700 italic mb-8">
                "Discover the experience that will stir your soul…"
              </p>
              <blockquote className="text-lg text-gray-600 mb-6">
                "What a fantastic way to see Uganda national park from the sky!"
                <footer className="text-sm text-gray-500 mt-2">– Trip Advisor October 2019</footer>
              </blockquote>
            </div>

            {/* Right Column */}
            <div>
              <p className="text-gray-700 leading-relaxed mb-6">
                As the pioneers of hot air balloon safaris in Uganda, SKYHIGH Adventures expertise and local knowledge is sought after and respected. Launched in 1976 by Alan Root from Keekerok Lodge we have been operating for more than 48 years in Uganda.
              </p>
              <p className="text-gray-700 leading-relaxed mb-8">
                Our team of international pilots have a combined experience of thousands of hours (the measure for expertise in piloting) flying hot air balloons commercially all over the world. Safety is at the heart of everything we do with British built ballooning equipment and annual rigorous safety checks by the Kenyan Civil Aviation Authority.
              </p>
              <Link
                to="/our-history"
                className="inline-block text-white font-semibold px-8 py-3 rounded transition-colors"
                style={{ backgroundColor: '#EE6A1F' }}
              >
                OUR HISTORY
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid - 4 Card Layout */}
      <section className="py-0 px-4 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Large Left Card - Magical Experience (spans 2 rows) */}
            <div 
              className="lg:row-span-2 relative overflow-hidden group"
              style={{ minHeight: '600px' }}
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=800)',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
              </div>
              <div className="relative z-10 h-full flex flex-col justify-end p-8 pb-16 text-white">
                <h3 className="text-4xl font-bold mb-4">Magical Experience</h3>
                <p className="text-lg mb-6 leading-relaxed">
                  Pioneers of Hot Air Ballooning in Kenya, knowledge of Uganda, passion for ballooning and Africa
                </p>
                <Link
                  to="/the-adventure"
                  className="inline-block text-center text-white font-semibold px-8 py-3 rounded transition-all hover:opacity-90 w-40"
                  style={{ backgroundColor: '#b8956a' }}
                >
                  DISCOVER
                </Link>
              </div>
            </div>

            {/* Top Right Card 1 - Why Choose Us */}
            <div 
              className="relative overflow-hidden group"
              style={{ minHeight: '290px' }}
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1761976671459-8a27e6b78c09?w=800)',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70"></div>
              </div>
              <div className="relative z-10 h-full flex flex-col justify-end p-6 text-white">
                <h3 className="text-2xl font-bold mb-3">WHY CHOOSE US?</h3>
                <div className="text-base mb-4 space-y-1">
                  <p>Experience</p>
                  <p>Pilot skill</p>
                  <p>Languages</p>
                </div>
                <Link
                  to="/our-pilots"
                  className="inline-block text-center text-white font-semibold px-6 py-2 rounded transition-all hover:opacity-90 w-36"
                  style={{ backgroundColor: '#b8956a' }}
                >
                  DISCOVER
                </Link>
              </div>
            </div>

            {/* Top Right Card 2 - Bucket List */}
            <div 
              className="relative overflow-hidden group"
              style={{ minHeight: '290px' }}
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1516886635086-2b3c423c0947?w=800)',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70"></div>
              </div>
              <div className="relative z-10 h-full flex flex-col justify-end p-6 text-white">
                <h3 className="text-2xl font-bold mb-3">BUCKET LIST</h3>
                <div className="text-base mb-4 space-y-1">
                  <p>Adventure of a Lifetime</p>
                  <p>Wow, wow, wow!</p>
                  <p>Sunrise in Uganda</p>
                </div>
                <Link
                  to="/reviews-gallery"
                  className="inline-block text-center text-white font-semibold px-6 py-2 rounded transition-all hover:opacity-90 w-36"
                  style={{ backgroundColor: '#b8956a' }}
                >
                  DISCOVER
                </Link>
              </div>
            </div>

            {/* Bottom Right Card - Safety (spans full width of right column) */}
            <div 
              className="lg:col-span-2 relative overflow-hidden group"
              style={{ minHeight: '290px' }}
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1200)',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70"></div>
              </div>
              <div className="relative z-10 h-full flex flex-col justify-end p-6 text-white">
                <h3 className="text-2xl font-bold mb-3">SAFETY</h3>
                <div className="text-base mb-4 space-y-1">
                  <p>Annual inspection by KCAA</p>
                  <p>Safety Processes</p>
                  <p>Head of Safety</p>
                </div>
                <Link
                  to="/our-balloons"
                  className="inline-block text-center text-white font-semibold px-6 py-2 rounded transition-all hover:opacity-90 w-36"
                  style={{ backgroundColor: '#b8956a' }}
                >
                  DISCOVER
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-12">
            Experience the ultimate safari from above
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-12">
            A place unchanged by the passage of time, Uganda is essential Africa. Everyone who travels here says that 'Africa gets into your blood'. It's so true! Rugged and dramatic, this is a wilderness of emotion, survival and inspiration; with wildlife drama unravelling before you at every turn. Often raw and sometimes violent, this wildlife theatre is there before your very eyes. There is nowhere else like it!
          </p>
        </div>
      </section>

      {/* Journey Section */}
      <section
        className="relative py-32 px-4 text-white text-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1723270868972-fed4b06feae4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwc3VucmlzZSUyMHNhdmFubmFofGVufDB8fHx8MTc2MzA3MTEwMHww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}></div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">A Journey for the Soul</h2>
          <p className="text-xl italic">
            "Something you can't get from a Jeep safari…"
            <br />
            <span className="text-sm">– Trip Advisor October 2019</span>
          </p>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">"100% worth it!"</h2>
          <p className="text-xl text-gray-600 mb-12">Reviewed 2024</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <p className="text-gray-700 italic">"{review.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 text-white text-center" style={{ backgroundColor: '#EE6A1F' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">Escape the ordinary with us</h2>
          <Link
            to="/contact"
            className="inline-block font-bold px-10 py-4 rounded text-lg transition-transform bg-[#EE6A1F] text-white border border-white/60 shadow-lg hover:scale-105"
          >
            ONLINE BOOKING ENQUIRY
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;
